import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useTheme } from '@/context/ThemeContext';

// ── Complex number utilities ──────────────────────────────────────────────────
const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;
const cadd    = (a, b) => ({ re: a.re + b.re, im: a.im + b.im });
const cmul    = (a, b) => ({ re: a.re*b.re - a.im*b.im, im: a.re*b.im + a.im*b.re });
const cscale  = (c, s) => ({ re: c.re*s, im: c.im*s });
const cmag    = (c)    => Math.sqrt(c.re*c.re + c.im*c.im);
const cang    = (c)    => Math.atan2(c.im, c.re) * RAD;
const fromPolar = (mag, angDeg) => ({ re: mag*Math.cos(angDeg*DEG), im: mag*Math.sin(angDeg*DEG) });
const A_OP    = fromPolar(1, 120);
const A2_OP   = fromPolar(1, 240);

function decompose(Va, Vb, Vc) {
  const V0 = cscale(cadd(Va, cadd(Vb, Vc)), 1/3);
  const V1 = cscale(cadd(Va, cadd(cmul(A_OP, Vb), cmul(A2_OP, Vc))), 1/3);
  const V2 = cscale(cadd(Va, cadd(cmul(A2_OP, Vb), cmul(A_OP, Vc))), 1/3);
  return { V0, V1, V2 };
}

// ── Mini sequence phasor diagram ──────────────────────────────────────────────
const SeqDiagram = ({ phasors, color, dark }) => {
  const CX = 70, CY = 70, R = 52;
  const bg   = dark ? '#0f172a' : '#ffffff';
  const axis = dark ? '#1e293b' : '#e8edf2';

  const drawPhasor = (vec, lbl) => {
    if (cmag(vec) < 0.02) return null;
    const mag   = Math.min(cmag(vec), 1.85);
    const rad   = Math.atan2(vec.im, vec.re);
    const scale = R / 1.0;
    const tx    = CX + mag * scale * Math.cos(rad);
    const ty    = CY - mag * scale * Math.sin(rad);
    const bx    = CX + (mag * scale - 10) * Math.cos(rad);
    const by    = CY - (mag * scale - 10) * Math.sin(rad);
    const hw    = 4;
    const pts   = `${tx},${ty} ${bx + hw*Math.sin(rad)},${by + hw*Math.cos(rad)} ${bx - hw*Math.sin(rad)},${by - hw*Math.cos(rad)}`;
    const lx    = tx + 13 * Math.cos(rad);
    const ly    = ty - 13 * Math.sin(rad) + 3.5;
    return (
      <g key={lbl}>
        <line x1={CX} y1={CY} x2={bx} y2={by} stroke={color} strokeWidth="2" strokeLinecap="round" />
        <polygon points={pts} fill={color} />
        <text x={lx} y={ly} fontSize="8.5" fontWeight="700" fontFamily="IBM Plex Mono" fill={color} textAnchor="middle">{lbl}</text>
      </g>
    );
  };

  return (
    <svg viewBox="0 0 140 140" className="w-full max-w-[120px] mx-auto block">
      <rect width="140" height="140" fill={bg} rx="6" />
      <circle cx={CX} cy={CY} r={R} fill="none" stroke={axis} strokeWidth="1" strokeDasharray="3 3" />
      <line x1={CX-R-4} y1={CY} x2={CX+R+4} y2={CY} stroke={axis} strokeWidth="0.8" />
      <line x1={CX} y1={CY-R-4} x2={CX} y2={CY+R+4} stroke={axis} strokeWidth="0.8" />
      <circle cx={CX} cy={CY} r="2.5" fill={dark ? '#475569' : '#94a3b8'} />
      {phasors.map(({ vec, label }) => drawPhasor(vec, label))}
    </svg>
  );
};

// ── Preset states ──────────────────────────────────────────────────────────────
const BALANCED  = { Va: fromPolar(1, 0), Vb: fromPolar(1, -120), Vc: fromPolar(1, 120) };
const SLG_FAULT = { Va: fromPolar(1, 0), Vb: { re: 0.001, im: 0 }, Vc: { re: 0.001, im: 0 } };
const PC = { Va: '#0d9488', Vb: '#6366f1', Vc: '#f59e0b' };

// ── Main Animator ─────────────────────────────────────────────────────────────
const SequenceAnimator = () => {
  const { dark } = useTheme();
  const svgRef    = useRef(null);
  const lastTRef  = useRef(null);
  const animRef   = useRef(null);

  const [phasors,   setPhasors]   = useState(BALANCED);
  const [dragging,  setDragging]  = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);

  const W = 330, H = 300, CX = W/2, CY = H/2, SCALE = 100;

  // ── Animation loop ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isPlaying || dragging) {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      lastTRef.current = null;
      return;
    }
    const OMEGA = 22; // deg/sec
    const animate = (ts) => {
      if (!lastTRef.current) lastTRef.current = ts;
      const dt = Math.min(0.05, (ts - lastTRef.current) / 1000);
      lastTRef.current = ts;
      const theta = OMEGA * dt * DEG;
      const cos = Math.cos(theta), sin = Math.sin(theta);
      const rot = c => ({ re: c.re*cos - c.im*sin, im: c.re*sin + c.im*cos });
      setPhasors(prev => ({ Va: rot(prev.Va), Vb: rot(prev.Vb), Vc: rot(prev.Vc) }));
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [isPlaying, dragging]);

  // ── Drag helpers ────────────────────────────────────────────────────────────
  const getSVGCoords = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return { x: CX, y: CY };
    const rect = svg.getBoundingClientRect();
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (cx - rect.left) * W / rect.width, y: (cy - rect.top) * H / rect.height };
  }, [W, H, CX, CY]);

  const fromScreen = useCallback((x, y) => {
    const raw = { re: (x - CX) / SCALE, im: -(y - CY) / SCALE };
    const mag = Math.min(1.85, Math.max(0.05, cmag(raw)));
    const ang = Math.atan2(raw.im, raw.re);
    return { re: mag*Math.cos(ang), im: mag*Math.sin(ang) };
  }, [CX, CY, SCALE]);

  const onPtrDown = useCallback((key, e) => {
    e.preventDefault(); e.stopPropagation();
    setIsPlaying(false); setDragging(key);
    if (svgRef.current && e.pointerId != null) svgRef.current.setPointerCapture(e.pointerId);
  }, []);

  const onPtrMove = useCallback((e) => {
    if (!dragging) return;
    const c = getSVGCoords(e);
    setPhasors(prev => ({ ...prev, [dragging]: fromScreen(c.x, c.y) }));
  }, [dragging, getSVGCoords, fromScreen]);

  const onPtrUp = useCallback(() => setDragging(null), []);

  const { Va, Vb, Vc } = phasors;
  const { V0, V1, V2 } = useMemo(() => decompose(Va, Vb, Vc), [Va, Vb, Vc]);

  // Sequence component phasors
  const seqPos  = useMemo(() => [
    { vec: V1, label: 'Va1' }, { vec: cmul(A2_OP, V1), label: 'Vb1' }, { vec: cmul(A_OP, V1), label: 'Vc1' },
  ], [V1]);
  const seqNeg  = useMemo(() => [
    { vec: V2, label: 'Va2' }, { vec: cmul(A_OP, V2), label: 'Vb2' }, { vec: cmul(A2_OP, V2), label: 'Vc2' },
  ], [V2]);
  const seqZero = useMemo(() => [{ vec: V0, label: 'V₀' }], [V0]);

  // Dynamic callout
  const msg = useMemo(() => {
    const m0 = cmag(V0), m1 = cmag(V1), m2 = cmag(V2);
    if (m1 > 0.8 && m0 < 0.04 && m2 < 0.04)
      return { type: 'balanced', text: 'Balanced set: V₀ ≈ 0, V₂ ≈ 0 — only positive-sequence remains. This is normal steady-state operation. Three-phase balanced analysis works perfectly here.' };
    if (cmag(Vb) < 0.06 && cmag(Vc) < 0.06)
      return { type: 'slg', text: 'Single-line-to-ground fault pattern (Phase B and C = 0). Notice: V₀ = V₁ = V₂ = Va/3 — all three sequence magnitudes are equal. This equal split is the unique mathematical fingerprint of an SLG fault.' };
    if (m0 > 0.3)
      return { type: 'zero', text: `Large zero-sequence component (purple, |V₀| = ${m0.toFixed(3)} pu). Zero-sequence current can only flow if there is a ground return path — a neutral connection or earth. It grows whenever a ground fault or neutral unbalance is present.` };
    if (m2 > 0.2)
      return { type: 'neg', text: `Significant negative-sequence component (orange, |V₂| = ${m2.toFixed(3)} pu). Negative sequence creates a reverse-rotating magnetic field. In motors and generators, this causes extra eddy-current heating — grid codes limit it to 1–2% of V₁.` };
    return { type: 'drag', text: 'Drag any phasor tip to create imbalance. Watch the positive (teal), negative (orange), and zero (purple) sequence components update instantly below — the live decomposition of Fortescue\'s theorem.' };
  }, [V0, V1, V2, Vb, Vc]);

  const bg    = dark ? '#0f172a' : '#ffffff';
  const ax    = dark ? '#1e293b' : '#e2e8f0';
  const lc    = dark ? '#64748b' : '#94a3b8';

  const msgCls = {
    balanced: 'bg-teal-50 dark:bg-teal-900/10 border-teal-200 dark:border-teal-800 text-teal-800 dark:text-teal-300',
    slg:      'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300',
    zero:     'bg-purple-50 dark:bg-purple-900/10 border-purple-200 dark:border-purple-800 text-purple-800 dark:text-purple-300',
    neg:      'bg-orange-50 dark:bg-orange-900/10 border-orange-200 dark:border-orange-800 text-orange-800 dark:text-orange-300',
    drag:     'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400',
  }[msg.type];

  return (
    <div data-testid="sequence-animator" className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap gap-2 items-center">
        <button data-testid="play-pause-btn" onClick={() => setIsPlaying(p => !p)}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
            isPlaying ? 'bg-teal-600 text-white border-teal-600'
            : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-teal-400'
          }`}>
          {isPlaying ? '⏸ Pause' : '▶ Play rotation'}
        </button>
        <button data-testid="make-balanced-btn" onClick={() => { setPhasors(BALANCED); setIsPlaying(false); }}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:border-teal-400 transition-colors">
          Make Balanced
        </button>
        <button data-testid="slg-fault-btn" onClick={() => { setPhasors(SLG_FAULT); setIsPlaying(false); }}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors">
          Simulate SLG Fault
        </button>
        <span className="text-xs text-slate-400 dark:text-slate-600 font-mono ml-1">
          {dragging ? `dragging ${dragging}` : isPlaying ? 'rotating...' : 'paused — drag tips'}
        </span>
      </div>

      {/* Main phasor SVG */}
      <div className="flex flex-col items-center">
        <p className="text-[10px] font-mono text-slate-500 dark:text-slate-500 mb-1.5 text-center">
          Phase voltages Va, Vb, Vc — drag phasor tips to create imbalance
        </p>
        <svg ref={svgRef} data-testid="main-phasor-svg"
          viewBox={`0 0 ${W} ${H}`}
          className="w-full max-w-sm border border-slate-200 dark:border-slate-700 rounded-xl"
          style={{ touchAction: 'none', cursor: dragging ? 'grabbing' : 'default' }}
          onPointerMove={onPtrMove} onPointerUp={onPtrUp} onPointerLeave={onPtrUp}>
          <rect width={W} height={H} fill={bg} rx="12" />
          {/* Grid */}
          {[0.5, 1.0, 1.5].map(f => (
            <circle key={f} cx={CX} cy={CY} r={SCALE*f} fill="none" stroke={ax} strokeWidth={f===1.0?1:0.7}
              strokeDasharray={f===1.0 ? '4 4' : '3 6'} />
          ))}
          <line x1={CX-SCALE*1.7} y1={CY} x2={CX+SCALE*1.7} y2={CY} stroke={ax} strokeWidth="1" />
          <line x1={CX} y1={CY-SCALE*1.7} x2={CX} y2={CY+SCALE*1.7} stroke={ax} strokeWidth="1" />
          <text x={CX+SCALE*1.7-4} y={CY-5} fontSize="9" fill={lc} fontFamily="IBM Plex Mono" textAnchor="end">+Re</text>
          <text x={CX+4} y={CY-SCALE*1.7+12} fontSize="9" fill={lc} fontFamily="IBM Plex Mono">+Im</text>
          <text x={CX+SCALE*0.5+3} y={CY-4} fontSize="7.5" fill={lc} fontFamily="IBM Plex Mono">0.5</text>
          <text x={CX+SCALE+3} y={CY-4} fontSize="7.5" fill={lc} fontFamily="IBM Plex Mono">1.0</text>
          <circle cx={CX} cy={CY} r="3.5" fill={dark ? '#475569' : '#94a3b8'} />

          {/* Phase phasors */}
          {Object.entries(phasors).map(([key, vec]) => {
            const color = PC[key];
            const mag   = Math.min(cmag(vec), 1.85);
            const rad   = Math.atan2(vec.im, vec.re);
            const tx    = CX + mag*SCALE*Math.cos(rad);
            const ty    = CY - mag*SCALE*Math.sin(rad);
            const bx    = CX + (mag*SCALE - 12)*Math.cos(rad);
            const by    = CY - (mag*SCALE - 12)*Math.sin(rad);
            const hw    = 5.5;
            const pts   = `${tx},${ty} ${bx+hw*Math.sin(rad)},${by+hw*Math.cos(rad)} ${bx-hw*Math.sin(rad)},${by-hw*Math.cos(rad)}`;
            const lx    = tx + 20*Math.cos(rad);
            const ly    = ty - 20*Math.sin(rad) + 4;
            return (
              <g key={key}>
                <line x1={CX} y1={CY} x2={bx} y2={by} stroke={color} strokeWidth="2.5" strokeLinecap="round" />
                <polygon points={pts} fill={color} />
                <circle cx={tx} cy={ty} r="10" fill={color} fillOpacity="0.18" stroke={color} strokeWidth="1.5"
                  style={{ cursor: 'grab' }}
                  onPointerDown={(e) => onPtrDown(key, e)} />
                <text x={lx} y={ly} fontSize="12" fontWeight="700" fontFamily="IBM Plex Mono" fill={color} textAnchor="middle">{key}</text>
                <text x={lx} y={ly+13} fontSize="8" fontFamily="IBM Plex Mono" fill={color} textAnchor="middle">
                  {mag.toFixed(2)}∠{cang(vec).toFixed(0)}°
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Sequence decomposition diagrams */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-2 text-center">
          Symmetrical decomposition — live
        </p>
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {[
            { id: 'pos',  color: '#0d9488', label: '(+) Positive', rot: 'ABC rotation', mag: cmag(V1), ang: cang(V1), seqPhasors: seqPos },
            { id: 'neg',  color: '#ea580c', label: '(−) Negative', rot: 'ACB rotation', mag: cmag(V2), ang: cang(V2), seqPhasors: seqNeg },
            { id: 'zero', color: '#7c3aed', label: '(0) Zero',     rot: 'All in phase', mag: cmag(V0), ang: cang(V0), seqPhasors: seqZero },
          ].map(({ id, color, label, rot, mag, ang, seqPhasors: sp }) => (
            <div key={id} data-testid={`${id}-seq-diagram`}
              className="flex flex-col items-center rounded-lg p-1.5 border"
              style={{ background: dark ? `${color}0a` : `${color}08`, borderColor: `${color}30` }}>
              <p className="text-[9px] font-bold font-mono mb-0.5 text-center" style={{ color }}>{label}</p>
              <p className="text-[9px] font-mono mb-1 text-center" style={{ color }}>
                {mag.toFixed(3)} pu ∠{ang.toFixed(1)}°
              </p>
              <SeqDiagram phasors={sp} color={color} dark={dark} />
              <p className="text-[8px] text-slate-400 dark:text-slate-600 text-center mt-1">{rot}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Dynamic callout */}
      <div data-testid="dynamic-callout" className={`rounded-lg p-3.5 border text-sm leading-relaxed ${msgCls}`}>
        {msg.text}
      </div>
    </div>
  );
};

export default SequenceAnimator;
